import { z } from 'zod';
import { asId, type AreaId } from '@shared/brand';
import {
  isOrgLinkKind,
  isOrgNodeKind,
  ORG_LABEL_MAX,
  type OrgChart,
  type OrgLink,
  type OrgNode,
} from '@domain/entities/OrgChart';
import { arrayOr, nullableText, numberOr, textOr, timestampSchema } from './base';

/**
 * El organigrama tal como llega de fuera — de Firestore, o del navegador en el
 * modo demo.
 *
 * Tolerante por tarjeta y por línea, como todo lo demás en `codecs/`: una tarjeta
 * ilegible se salta y el resto del dibujo se enseña. Lo único que no se tolera es
 * una línea que apunte a una tarjeta que no está, porque el lienzo la pintaría
 * saliendo de la nada.
 */

const nodeSchema = z
  .object({
    id: z.string().min(1),
    kind: z.unknown(),
    x: numberOr(0),
    y: numberOr(0),
    personId: nullableText(),
    subAreaId: nullableText(),
    label: textOr(''),
  })
  .transform((raw): OrgNode | null => {
    if (!isOrgNodeKind(raw.kind)) return null;
    /* Una tarjeta de persona sin persona no dice nada: se pinta como vacante. */
    const kind = raw.kind === 'persona' && raw.personId === null ? 'vacante' : raw.kind;
    return {
      id: raw.id,
      kind,
      x: Math.round(raw.x),
      y: Math.round(raw.y),
      personId: kind === 'persona' && raw.personId !== null ? asId<'PersonId'>(raw.personId) : null,
      subAreaId:
        kind === 'equipo' && raw.subAreaId !== null ? asId<'SubAreaId'>(raw.subAreaId) : null,
      label: raw.label.slice(0, ORG_LABEL_MAX),
    };
  });

const linkSchema = z
  .object({
    id: z.string().min(1),
    kind: z.unknown(),
    from: z.string().min(1),
    to: z.string().min(1),
  })
  .transform((raw): OrgLink | null =>
    isOrgLinkKind(raw.kind) ? { id: raw.id, kind: raw.kind, from: raw.from, to: raw.to } : null,
  );

const chartSchema = z.object({
  nodes: arrayOr(nodeSchema),
  links: arrayOr(linkSchema),
  updatedAt: timestampSchema,
  updatedByName: nullableText(),
});

export const decodeOrgChart = (areaId: AreaId, data: unknown): OrgChart | null => {
  const parsed = chartSchema.safeParse(data);
  if (!parsed.success) return null;

  const nodes = parsed.data.nodes.filter((node): node is OrgNode => node !== null);
  const present = new Set(nodes.map((node) => node.id));
  const links = parsed.data.links.filter(
    (link): link is OrgLink => link !== null && present.has(link.from) && present.has(link.to),
  );

  return {
    areaId,
    nodes,
    links,
    updatedAt: parsed.data.updatedAt,
    updatedByName: parsed.data.updatedByName,
  };
};
